import React, { useState } from 'react';
import { Pressable, type GestureResponderEvent } from 'react-native';
import { IconEye, IconNotes } from '@tabler/icons-react-native';
import { Box, Text, useTheme } from 'app/shopify/theme';
import { ICON_STROKE_WIDTH } from 'app/utils/constants';

export interface DocumentAttachmentProps {
  name: string;
  previewLabel: string;
  previewAccessibilityLabel?: string;
  onPreview: (event: GestureResponderEvent) => void;
  disabled?: boolean;
}

/** Attached-document row from the Mutuo sharing-fee form. */
export function DocumentAttachment({
  name,
  previewLabel,
  previewAccessibilityLabel,
  onPreview,
  disabled = false,
}: DocumentAttachmentProps) {
  const theme = useTheme();
  const [focused, setFocused] = useState(false);
  const [hovered, setHovered] = useState(false);
  return (
    <Box
      flexDirection="row"
      alignItems="center"
      borderWidth={1}
      borderColor="border.primary"
      borderRadius="radius.lg"
      // The reference uses 15px padding and a 10px gap, between the spacing scale's steps.
      style={{ padding: 15, gap: 10 }}
    >
      <IconNotes size={30} color={theme.colors['text.primary']} strokeWidth={ICON_STROKE_WIDTH} />
      <Text variant="bodyBaseStrong" color="text.primary" flex={1} style={{ minWidth: 0 }}>
        {name}
      </Text>
      <Pressable
        accessibilityRole="button"
        accessibilityLabel={previewAccessibilityLabel ?? `${previewLabel}: ${name}`}
        accessibilityState={{ disabled }}
        disabled={disabled}
        onPress={onPreview}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        onHoverIn={() => setHovered(true)}
        onHoverOut={() => setHovered(false)}
        style={({ pressed }) => ({
          borderRadius: theme.borderRadii['radius.full'],
          opacity: disabled ? 0.4 : pressed ? 0.7 : hovered ? 0.85 : 1,
          flexShrink: 0,
        })}
      >
        <Box
          flexDirection="row"
          alignItems="center"
          justifyContent="center"
          backgroundColor="surface.brand.bold"
          borderRadius="radius.full"
          minHeight={40}
          paddingHorizontal="spacing.general.x4"
        >
          <IconEye size={24} color={theme.colors['text.inverse.primary']} strokeWidth={ICON_STROKE_WIDTH} />
          <Text variant="bodyBaseStrong" color="text.inverse.primary" paddingHorizontal="spacing.general.x3">
            {previewLabel}
          </Text>
        </Box>
        {focused ? (
          <Box
            pointerEvents="none"
            position="absolute"
            top={-4}
            bottom={-4}
            left={-4}
            right={-4}
            borderWidth={2}
            borderColor="border.focused"
            borderRadius="radius.full"
          />
        ) : null}
      </Pressable>
    </Box>
  );
}
